import React from 'react';
import {
  X, Calendar, Clock, MapPin, Navigation, Users, Baby, Briefcase, Phone, Mail,
  CreditCard, Plane, MessageSquare, Tag, ExternalLink, Car
} from 'lucide-react';
import { formatPickup } from '../../utils/datetime';

export function BookingDetailModal({ booking, onClose, t }) {
  if (!booking) return null;
  const transfer = booking.transfer || {};
  const pickup = transfer.pickup_location || {};
  const dropoff = transfer.dropoff_location || {};

  const adults = transfer.adults || 0;
  const children = transfer.children || 0;
  const totalPax = adults + children;
  const childSeats = transfer.child_seats || 0;
  const luggage = transfer.luggage || 0;

  const openInMaps = (lat, lng) => {
    if (!lat || !lng) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  };

  const callPhone = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const writeWhatsapp = (phone) => {
    if (!phone) return;
    const cleaned = phone.replace(/[^0-9+]/g, '');
    window.open(`https://wa.me/${cleaned.replace('+', '')}`, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 sm:flex sm:items-center sm:justify-center sm:p-4"
      onClick={onClose}
      data-testid="booking-detail-modal"
    >
      <div
        className="bg-white sm:rounded-2xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-slate-800 truncate">
              {t('driver_booking_detail_title') || 'Buchung Details'}
            </h2>
            <p className="text-xs text-slate-400 truncate">ID: {transfer.id || booking.transfer_id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-2 -mr-2 shrink-0"
            aria-label="Close"
            data-testid="booking-detail-close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 flex-1 overflow-y-auto overflow-x-hidden space-y-4">
          {/* Price + Status */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-700 font-medium uppercase">Mein Preis</p>
              <p className="text-2xl font-bold text-emerald-700">€{booking.price}</p>
            </div>
            <div className="px-3 py-1.5 bg-white rounded-lg text-sm font-medium text-emerald-700 border border-emerald-200">
              {transfer.status === 'completed'
                ? (t('driver_status_completed') || 'Abgeschlossen')
                : (t('driver_status_booked') || 'Gebucht')}
            </div>
          </div>

          {/* Route */}
          <Section title="Route">
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center pt-1 shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <div className="w-0.5 h-12 bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                </div>
                <div className="flex-1 min-w-0 space-y-3">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Abholung</p>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm text-slate-800 truncate flex-1">{pickup.address || '-'}</p>
                      {pickup.lat && pickup.lng && (
                        <button onClick={() => openInMaps(pickup.lat, pickup.lng)} className="text-blue-500 hover:text-blue-700 shrink-0" data-testid="open-pickup-maps">
                          <ExternalLink size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Ziel</p>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm text-slate-800 truncate flex-1">{dropoff.address || '-'}</p>
                      {dropoff.lat && dropoff.lng && (
                        <button onClick={() => openInMaps(dropoff.lat, dropoff.lng)} className="text-blue-500 hover:text-blue-700 shrink-0" data-testid="open-dropoff-maps">
                          <ExternalLink size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Navigation size={12} /> {transfer.distance_km || 0} km</span>
                <span className="flex items-center gap-1"><Clock size={12} /> ~{transfer.duration_minutes || 0} Min.</span>
              </div>
            </div>
          </Section>

          {/* Date + Flight */}
          <Section title="Datum & Flug">
            <div className="grid grid-cols-2 gap-3">
              <InfoCard icon={<Calendar size={14} />} label="Datum & Zeit">
                {formatPickup(transfer.pickup_date) || '-'}
              </InfoCard>
              <InfoCard icon={<Plane size={14} />} label="Flugnummer">
                {transfer.flight_number || '—'}
              </InfoCard>
            </div>
            {transfer.name_on_sign && (
              <InfoCard icon={<Tag size={14} />} label="Schild-Name">
                {transfer.name_on_sign}
              </InfoCard>
            )}
          </Section>

          {/* Passengers + Luggage */}
          <Section title="Personen & Gepäck">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoCard icon={<Users size={14} />} label="Erwachsene" value={adults} />
              <InfoCard icon={<Baby size={14} />} label="Kinder" value={children} />
              <InfoCard icon={<Baby size={14} />} label="Kindersitze" value={childSeats} />
              <InfoCard icon={<Briefcase size={14} />} label="Gepäck" value={luggage} />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Total: {totalPax} {totalPax === 1 ? 'Person' : 'Personen'}
            </p>
          </Section>

          {/* Customer Contact */}
          <Section title="Kunde">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold shrink-0">
                  {(transfer.customer_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{transfer.customer_name || '-'}</p>
                  <p className="text-xs text-slate-500">Kunde</p>
                </div>
              </div>

              {transfer.customer_phone && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => callPhone(transfer.customer_phone)}
                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-blue-200 text-blue-700 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-50"
                    data-testid="call-customer"
                  >
                    <Phone size={14} /> {transfer.customer_phone}
                  </button>
                  <button
                    onClick={() => writeWhatsapp(transfer.customer_phone)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2.5 rounded-lg shrink-0"
                    title="WhatsApp"
                    data-testid="whatsapp-customer"
                  >
                    <MessageSquare size={14} />
                  </button>
                </div>
              )}

              {transfer.customer_email && (
                <a
                  href={`mailto:${transfer.customer_email}`}
                  className="flex items-center gap-2 bg-white border border-blue-200 text-blue-700 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-50"
                  data-testid="email-customer"
                >
                  <Mail size={14} /> <span className="truncate">{transfer.customer_email}</span>
                </a>
              )}
            </div>
          </Section>

          {/* Vehicle */}
          {(booking.vehicle || transfer.preferred_vehicle_type) && (
            <Section title="Fahrzeug">
              <InfoCard icon={<Car size={14} />} label="Fahrzeug">
                {booking.vehicle
                  ? `${booking.vehicle.make || ''} ${booking.vehicle.model || ''}`.trim()
                  : (transfer.preferred_vehicle_type || '-')}
              </InfoCard>
            </Section>
          )}

          {/* Notes */}
          {(transfer.comment || transfer.customer_notes || booking.notes) && (
            <Section title="Notizen">
              {transfer.comment && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-[10px] text-amber-700 uppercase font-semibold mb-1">Vom Kunden</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{transfer.comment}</p>
                </div>
              )}
              {booking.notes && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Meine Notiz</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{booking.notes}</p>
                </div>
              )}
            </Section>
          )}

          {/* Payment */}
          <Section title="Zahlung">
            <InfoCard icon={<CreditCard size={14} />} label="Status">
              {transfer.payment_status === 'paid' ? '✅ Bezahlt' : '⏳ Ausstehend'}
            </InfoCard>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">{title}</h3>
      {children}
    </section>
  );
}

function InfoCard({ icon, label, value, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 uppercase mb-1">
        {icon} {label}
      </div>
      <p className="text-sm font-medium text-slate-800 truncate">{value ?? children ?? '—'}</p>
    </div>
  );
}
